import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  BookOpen,
  FolderOpen,
  LayoutDashboard,
  LogOut,
  Mail,
  Mic,
  Play,
  Search,
  Settings,
  Shield,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import apiService from "@/services/apiService";
import authService from "@/services/authService";
import InboxBell from "@/components/shared/InboxBell";
import ThemeToggle from "@/components/shared/ThemeToggle";
import { toast } from "sonner";

function SidebarItem({ icon: Icon, label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`flex w-full items-center gap-4 rounded-xl px-4 py-3 text-left text-sm font-medium transition ${
        active
          ? "bg-gradient-to-r from-[#7a5200] to-[#c99220] text-white shadow-[0_0_24px_rgba(218,165,32,0.25)]"
          : "text-white/80 hover:bg-white/10 hover:text-white"
      }`}
    >
      <Icon className="h-5 w-5 text-[#f2bd3b]" />
      <span className="flex-1">{label}</span>
    </button>
  );
}

function BookCard({ book, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(book.id)}
      className="group overflow-hidden rounded-2xl border border-[#eadfcd] bg-white/95 p-5 text-left shadow-[0_16px_45px_rgba(94,64,18,0.08)] transition hover:-translate-y-0.5 hover:border-[#d8bd7a] hover:shadow-[0_22px_60px_rgba(94,64,18,0.14)] dark:border-[#4d3d16] dark:bg-[#0d0c09]/95 dark:shadow-[0_16px_45px_rgba(0,0,0,0.36)] dark:hover:border-[#8a6a24]"
    >
      <div className="mb-5 flex items-start justify-between gap-4">
        <div className="grid h-16 w-16 place-items-center rounded-2xl bg-gradient-to-br from-[#f6d77f] to-[#b77800] text-2xl font-bold text-black shadow-md">
          {book.title?.charAt(0) || "B"}
        </div>
        <Badge className={book.launched ? "bg-green-100 text-green-800" : "bg-[#fff2d8] text-[#9a6500]"}>
          {book.launched ? "Launched" : "Book"}
        </Badge>
      </div>

      <h3
        className="line-clamp-2 text-2xl font-bold text-slate-950 dark:text-[#f8efe0]"
        style={{ fontFamily: "Playfair Display, serif" }}
      >
        {book.title || "Untitled Book"}
      </h3>
      <p className="mt-3 text-sm text-slate-600 dark:text-[#9f947f]">
        {[book.country, book.language].filter(Boolean).join(" - ") || "Golden Records"}
      </p>
      {book.description_short ? (
        <p className="mt-4 line-clamp-3 text-sm leading-6 text-slate-600 dark:text-[#d5c8ae]">
          {book.description_short}
        </p>
      ) : null}

      <div className="mt-6 flex items-center justify-between border-t border-[#f0e4d2] pt-4 dark:border-[#2f260f]">
        <span className="text-sm font-semibold text-[#b77900] dark:text-[#f2c95d]">
          View details
        </span>
        <ArrowRight className="h-4 w-4 text-[#c98905] transition group-hover:translate-x-1" />
      </div>
    </button>
  );
}

export default function Books() {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedBook, setSelectedBook] = useState(null);
  const [showBookModal, setShowBookModal] = useState(false);
  const [user, setUser] = useState(null);

  const isAdmin = user?.role?.toLowerCase() === "admin";

  useEffect(() => {
    loadBooks();
    authService.getMe().then(setUser).catch(() => setUser(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadBooks = async (search = "") => {
    try {
      setLoading(true);
      const data = await apiService.getLaunchedBooks(search);
      setBooks(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error loading books:", error);
      toast.error("Failed to load books");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    const value = event.target.value;
    setQuery(value);
    loadBooks(value);
  };

  const handleOpenBook = async (bookId) => {
    try {
      const detail = await apiService.getBookDetail(bookId);
      setSelectedBook(detail);
      setShowBookModal(true);
    } catch (error) {
      toast.error("Failed to load book details");
    }
  };

  const handleLogout = async () => {
    await authService.logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#f8f0df] text-slate-950 dark:bg-[#050504] dark:text-[#f8efe0]">
      <div className="flex min-h-screen">
        <aside className="hidden w-[300px] shrink-0 bg-[#070806] text-white shadow-2xl xl:block">
          <div className="sticky top-0 flex h-screen flex-col gap-8 overflow-y-auto px-6 py-7">
            <button
              className="flex w-full items-center text-left"
              onClick={() => navigate("/dashboard")}
            >
              <img
                src="/images/golden-records-real-logo.png"
                alt="Golden Records"
                className="h-36 w-full rounded-xl object-cover object-center"
              />
            </button>

            <nav className="space-y-2">
              <SidebarItem
                icon={LayoutDashboard}
                label="Dashboard"
                onClick={() => navigate("/dashboard")}
              />
              <SidebarItem icon={BookOpen} label="Books" active onClick={() => navigate("/books")} />
              <SidebarItem
                icon={FolderOpen}
                label="Projects"
                onClick={() => navigate("/publisher/workflow")}
              />
              <SidebarItem icon={Mic} label="Narrators" onClick={() => navigate("/narrator")} />
              <SidebarItem icon={FolderOpen} label="Work" onClick={() => navigate("/work")} />
              <SidebarItem
                icon={BarChart3}
                label="Publisher"
                onClick={() => navigate("/publisher/workflow")}
              />
              <SidebarItem icon={Mail} label="Inbox" onClick={() => navigate("/inbox")} />
              <SidebarItem icon={Settings} label="Settings" />
              {isAdmin ? (
                <SidebarItem icon={Shield} label="Admin" onClick={() => navigate("/admin")} />
              ) : null}
            </nav>
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <header className="sticky top-0 z-40 border-b border-[#e9dcc5] bg-white/90 px-4 py-4 backdrop-blur dark:border-[#2f260f] dark:bg-[#070705]/95 md:px-8">
            <div className="flex items-center gap-4">
              <button className="xl:hidden" onClick={() => navigate("/dashboard")}>
                <span
                  className="text-xl font-bold dark:text-[#f8efe0]"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  Golden Records
                </span>
              </button>

              <div className="relative ml-auto hidden w-full max-w-3xl md:block">
                <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search all books..."
                  value={query}
                  onChange={handleSearch}
                  className="h-12 rounded-2xl border-[#eadfcd] bg-white pl-12 pr-12 shadow-sm dark:border-[#594719] dark:bg-[#11100d] dark:text-[#f8efe0] dark:placeholder:text-[#8f8677]"
                />
                <Search className="absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#c98905]" />
              </div>

              <ThemeToggle />
              <InboxBell className="relative rounded-full p-3 text-slate-600 hover:bg-[#fff7e9] dark:text-[#f2c95d] dark:hover:bg-[#1b1710]" />
              <Button
                variant="outline"
                onClick={handleLogout}
                className="hidden border-[#e8dcc8] bg-white hover:bg-[#fff8eb] dark:border-[#8a6a24] dark:bg-[#11100d] dark:text-[#f2c95d] dark:hover:bg-[#1b1710] md:inline-flex"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </Button>
            </div>
          </header>

          <main className="relative z-10 space-y-8 px-4 py-7 md:px-8">
            <section className="relative overflow-hidden rounded-[28px] border border-[#eadfcd] bg-white shadow-[0_24px_80px_rgba(129,91,21,0.10)] dark:border-[#4d3d16] dark:bg-[#0d0c09] dark:shadow-[0_24px_80px_rgba(0,0,0,0.45)]">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_20%,rgba(245,207,117,0.28),transparent_25%),linear-gradient(135deg,#ffffff_0%,#fff7e8_100%)] dark:bg-[radial-gradient(circle_at_85%_20%,rgba(224,165,27,0.26),transparent_25%),linear-gradient(135deg,#0d0c09_0%,#070705_100%)]" />
              <div className="relative px-7 py-10 md:px-10">
                <p className="text-sm font-semibold uppercase tracking-[0.35em] text-[#b77900] dark:text-[#f2c95d]">
                  Golden Records Library
                </p>
                <h1
                  className="mt-5 text-4xl font-bold leading-tight text-slate-950 dark:text-[#f8efe0] md:text-6xl"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  All Books
                </h1>
                <p className="mt-4 max-w-2xl text-lg leading-8 text-slate-700 dark:text-[#d5c8ae]">
                  Browse every preserved book and launched audiobook in the Golden Records collection.
                </p>
              </div>
            </section>

            {loading ? (
              <div className="flex h-[320px] items-center justify-center">
                <div className="text-center">
                  <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-[#b77900]" />
                  <p className="mt-4 text-slate-600 dark:text-[#d5c8ae]">Loading books...</p>
                </div>
              </div>
            ) : books.length === 0 ? (
              <div className="rounded-2xl border border-[#eadfcd] bg-white/95 px-6 py-16 text-center shadow-[0_18px_55px_rgba(82,55,12,0.08)] dark:border-[#4d3d16] dark:bg-[#0d0c09]/95">
                <BookOpen className="mx-auto h-16 w-16 text-[#c98905]" />
                <h2
                  className="mt-5 text-2xl font-semibold text-slate-950 dark:text-[#f8efe0]"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  No books found
                </h2>
                <p className="mt-2 text-slate-600 dark:text-[#d5c8ae]">
                  Try another search or check back after books are launched.
                </p>
              </div>
            ) : (
              <section className="grid grid-cols-1 gap-5 md:grid-cols-2 2xl:grid-cols-4">
                {books.map((book) => (
                  <BookCard key={book.id} book={book} onOpen={handleOpenBook} />
                ))}
              </section>
            )}
          </main>
        </div>
      </div>

      <Dialog open={showBookModal} onOpenChange={setShowBookModal}>
        <DialogContent className="max-w-2xl bg-white dark:border-[#4d3d16] dark:bg-[#0d0c09] dark:text-[#f8efe0]">
          {selectedBook ? (
            <>
              <DialogHeader>
                <DialogTitle
                  className="text-2xl text-slate-950 dark:text-[#f8efe0]"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  {selectedBook.title}
                </DialogTitle>
                <DialogDescription className="dark:text-[#d5c8ae]">
                  {selectedBook.description_short}
                </DialogDescription>
              </DialogHeader>

              <div className="mt-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-lg bg-[#fff8eb] p-3 dark:bg-[#15120b]">
                    <p className="text-sm text-slate-600 dark:text-[#9f947f]">Country</p>
                    <p className="font-semibold text-slate-900 dark:text-[#f8efe0]">
                      {selectedBook.country || "Unknown"}
                    </p>
                  </div>
                  <div className="rounded-lg bg-[#fff8eb] p-3 dark:bg-[#15120b]">
                    <p className="text-sm text-slate-600 dark:text-[#9f947f]">Language</p>
                    <p className="font-semibold text-slate-900 dark:text-[#f8efe0]">
                      {selectedBook.language || "Unknown"}
                    </p>
                  </div>
                </div>

                {selectedBook.listen_url ? (
                  <a href={selectedBook.listen_url} target="_blank" rel="noopener noreferrer">
                    <Button className="w-full bg-[#c98905] text-white hover:bg-[#a56f00]">
                      <Play className="mr-2 h-4 w-4 fill-white" />
                      Experience on Lysten AI
                    </Button>
                  </a>
                ) : (
                  <div className="rounded-lg border border-[#eadfcd] bg-[#fff8eb] p-4 text-sm text-slate-600 dark:border-[#4d3d16] dark:bg-[#15120b] dark:text-[#d5c8ae]">
                    Audio link is not available yet.
                  </div>
                )}
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
